// Task type definitions

export enum TaskType {
  PROCUREMENT = 'procurement',
  PICKING = 'picking',
  PACKING = 'packing',
  LOADING = 'loading',
  DELIVERY = 'delivery',
  INSTALLATION = 'installation',
  QUALITY_ASSURANCE = 'quality_assurance',
  INSPECTION = 'inspection',
  UNLOADING = 'unloading',
  DOCUMENTATION = 'documentation',
  CUSTOMER_SIGNATURE = 'customer_signature',
  RETURNS_PROCESSING = 'returns_processing',
  INVENTORY_RECONCILIATION = 'inventory_reconciliation',
}

export enum TaskStatus {
  PENDING = 'pending',
  ASSIGNED = 'assigned',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  BLOCKED = 'blocked',
  CANCELLED = 'cancelled',
}

export enum TaskPriority {
  LOW = 'low',
  NORMAL = 'normal',
  HIGH = 'high',
  URGENT = 'urgent',
}

export type TaskCreateInput = {
  task_number?: string;
  task_type: TaskType;
  task_order: number;
  job_id: string;
  assigned_to_user_id?: string | null;
  priority?: TaskPriority;
  planned_start_at?: Date | null;
  planned_end_at?: Date | null;
  estimated_duration_hours?: number | null;
  instructions?: string | null;
  requires_photo?: boolean;
  created_by_user_id: string;
  task_template_id?: string | null;
  depends_on_task_ids?: string[] | null;
};

export type TaskUpdateInput = Partial<TaskCreateInput> & {
  status?: TaskStatus;
  actual_start_at?: Date | null;
  actual_end_at?: Date | null;
  completion_notes?: string | null;
  location_lat?: number | null;
  location_lon?: number | null;
  location_updated_at?: Date | null;
};

export type Task = {
  id: string;
  task_number: string;
  task_type: TaskType;
  task_order: number;
  job_id: string;
  assigned_to_user_id?: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  planned_start_at?: string | null;
  planned_end_at?: string | null;
  actual_start_at?: string | null;
  actual_end_at?: string | null;
  estimated_duration_hours?: number | null;
  instructions?: string | null;
  completion_notes?: string | null;
  requires_photo: boolean;
  location_lat?: number | null;
  location_lon?: number | null;
  location_updated_at?: string | null;
  created_by_user_id: string;
  task_template_id?: string | null;
  depends_on_task_ids?: string[] | null;
  created_at: string;
  updated_at: string;
  // Associations
  job?: import('./job').Job;
  assigned_to_user?: import('./shipment').User;
  created_by_user?: import('./shipment').User;
  task_template?: TaskTemplate;
  checklist_items?: TaskChecklistItem[];
  comments?: TaskComment[];
  photos?: TaskPhoto[];
  checklist_progress?: {
    total: number;
    completed: number;
    percentage: number;
  };
};

export type TaskTemplate = {
  id: string;
  name: string;
  task_type: TaskType;
  description?: string | null;
  default_instructions?: string | null;
  default_priority: TaskPriority;
  estimated_duration_hours?: number | null;
  requires_photo: boolean;
  requires_checklist_completion: boolean;
  is_active: boolean;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
  // Associations
  created_by_user?: import('./shipment').User;
  template_items?: TaskTemplateItem[];
};

export type TaskTemplateItem = {
  id: string;
  task_template_id: string;
  label: string;
  description?: string | null;
  item_order: number;
  is_required: boolean;
  links_to_order_items: boolean;
  created_at: string;
  updated_at: string;
};

export type TaskChecklistItem = {
  id: string;
  task_id: string;
  label: string;
  description?: string | null;
  item_order: number;
  is_required: boolean;
  is_completed: boolean;
  completed_at?: string | null;
  completed_by_user_id?: string | null;
  order_item_id?: string | null;
  created_at: string;
  updated_at: string;
  // Associations
  completed_by_user?: import('./shipment').User;
  order_item?: import('./orderItem').OrderItem;
};

export type TaskComment = {
  id: string;
  task_id: string;
  user_id: string;
  comment: string;
  mentions?: string[] | null;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
  // Associations
  user?: import('./shipment').User;
};

export type TaskPhoto = {
  id: string;
  task_id: string;
  key: string;
  original_filename?: string | null;
  content_type?: string | null;
  size_bytes?: number | null;
  sha256?: string | null;
  exif_json?: Record<string, unknown> | null;
  width?: number | null;
  height?: number | null;
  uploaded_by_user_id?: string | null;
  created_at: string;
  // Associations
  uploaded_by_user?: import('./shipment').User;
  // Added by service when fetching
  url?: string;
};

export type TaskActivity = {
  id: string;
  type: 'comment' | 'activity' | 'checklist';
  activity_type?: string;
  field_name?: string;
  old_value?: string;
  new_value?: string;
  content?: string;
  mentions?: string[] | null;
  user?: import('./shipment').User | null;
  created_at: string;
  updated_at?: string;
};

export type TaskActivityLogEntry = {
  id: string;
  task_id: string;
  user_id: string;
  activity_type: string;
  field_name?: string;
  old_value?: string;
  new_value?: string;
  created_at: string;
  user?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
};

export type TaskStats = {
  total: number;
  pending: number;
  assigned: number;
  in_progress: number;
  completed: number;
  blocked: number;
  cancelled: number;
  overdue: number;
  my_tasks?: number;
  byType: {
    [key in TaskType]: number;
  };
  byPriority: {
    low: number;
    normal: number;
    high: number;
    urgent: number;
  };
};

export { TaskType as default };
